import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import type {
  ProductAllocation,
  ProductGroup,
} from "@/marketing/models/influencerCampaign";

import {
  MAX_PRODUCT_ALLOCATIONS,
  MAX_PRODUCT_GROUPS,
  createProductGroupId,
  validateProductGroupConfiguration,
} from "./productGroupForm";

interface ProductGroupsEditorProps {
  productGroups: ProductGroup[];
  productAllocations: ProductAllocation[];
  onProductGroupsChange: (productGroups: ProductGroup[]) => void;
  onProductAllocationsChange: (productAllocations: ProductAllocation[]) => void;
}

interface ProductGroupOptionsEditorProps extends ProductGroupsEditorProps {
  productGroup: ProductGroup;
}

function ProductGroupOptionsEditor({
  productGroup,
  productGroups,
  productAllocations,
  onProductGroupsChange,
  onProductAllocationsChange,
}: ProductGroupOptionsEditorProps) {
  const [skuDraft, setSkuDraft] = useState("");
  const [quantityDraft, setQuantityDraft] = useState("");
  const groupLabel = productGroup.name?.trim() || productGroup.groupId;
  const groupAllocations = productAllocations.filter(
    (allocation) => allocation.groupId === productGroup.groupId,
  );
  const normalizedSkuDraft = skuDraft.trim();
  const quantity = Number(quantityDraft);
  const isDuplicateSku = productAllocations.some(
    (allocation) => allocation.sku === normalizedSkuDraft,
  );
  const isAddOptionDisabled =
    !normalizedSkuDraft ||
    isDuplicateSku ||
    !Number.isInteger(quantity) ||
    quantity < 1 ||
    quantity > 100 ||
    productAllocations.length >= MAX_PRODUCT_ALLOCATIONS;

  const handleGroupNameChange = (name: string) => {
    onProductGroupsChange(
      productGroups.map((existingGroup) =>
        existingGroup.groupId === productGroup.groupId
          ? { ...existingGroup, name }
          : existingGroup,
      ),
    );
  };

  const handleChoiceCountChange = (rawChoiceCount: string) => {
    const choiceCount = Number(rawChoiceCount);
    onProductGroupsChange(
      productGroups.map((existingGroup) =>
        existingGroup.groupId === productGroup.groupId
          ? { ...existingGroup, choiceCount }
          : existingGroup,
      ),
    );
  };

  const handleAddOption = () => {
    if (isAddOptionDisabled) return;
    onProductAllocationsChange([
      ...productAllocations,
      {
        sku: normalizedSkuDraft,
        quantity,
        groupId: productGroup.groupId,
        disabled: false,
      },
    ]);
    setSkuDraft("");
    setQuantityDraft("");
  };

  const handleDisabledChange = (
    productAllocation: ProductAllocation,
    disabled: boolean,
  ) => {
    onProductAllocationsChange(
      productAllocations.map((existingAllocation) =>
        existingAllocation.sku === productAllocation.sku
          ? { ...existingAllocation, disabled }
          : existingAllocation,
      ),
    );
  };

  const handleQuantityChange = (
    productAllocation: ProductAllocation,
    rawQuantity: string,
  ) => {
    const nextQuantity = Number(rawQuantity);
    onProductAllocationsChange(
      productAllocations.map((existingAllocation) =>
        existingAllocation.sku === productAllocation.sku
          ? { ...existingAllocation, quantity: nextQuantity }
          : existingAllocation,
      ),
    );
  };

  const handleRemoveOption = (sku: string) => {
    onProductAllocationsChange(
      productAllocations.filter((allocation) => allocation.sku !== sku),
    );
  };

  const handleRemoveGroup = () => {
    onProductGroupsChange(
      productGroups.filter(
        (existingGroup) => existingGroup.groupId !== productGroup.groupId,
      ),
    );
    onProductAllocationsChange(
      productAllocations.filter(
        (allocation) => allocation.groupId !== productGroup.groupId,
      ),
    );
  };

  return (
    <section className="tw-grid tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-background tw-p-3.5">
      <div className="tw-flex tw-items-start tw-gap-3">
        <div className="tw-grid tw-flex-1 tw-gap-1.5">
          <label className="tw-text-[11px] tw-font-semibold tw-text-muted-foreground">
            Group name
          </label>
          <Input
            aria-label={`Group name for ${productGroup.groupId}`}
            value={productGroup.name ?? ""}
            maxLength={255}
            onChange={(event) => handleGroupNameChange(event.target.value)}
            placeholder="e.g. Lip color"
          />
          <span className="tw-font-mono tw-text-[10px] tw-text-muted-foreground">
            {productGroup.groupId}
          </span>
        </div>
        <div className="tw-w-28">
          <label className="tw-mb-1.5 tw-block tw-text-[11px] tw-font-semibold tw-text-muted-foreground">
            Creator picks
          </label>
          <Input
            aria-label={`Choice count for ${groupLabel}`}
            type="number"
            min={1}
            max={50}
            value={productGroup.choiceCount}
            onChange={(event) => handleChoiceCountChange(event.target.value)}
          />
        </div>
        <button
          type="button"
          aria-label={`Remove product group ${groupLabel}`}
          onClick={handleRemoveGroup}
          className="tw-mt-6 tw-text-muted-foreground hover:tw-text-destructive"
        >
          <Trash2 className="tw-h-4 tw-w-4" />
        </button>
      </div>

      {groupAllocations.length > 0 && (
        <div className="tw-grid tw-gap-1.5">
          {groupAllocations.map((allocation) => (
            <div
              key={allocation.sku}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-border tw-bg-muted/20 tw-px-2.5 tw-py-2 tw-text-sm"
            >
              <span className="tw-flex-1 tw-truncate" title={allocation.sku}>
                {allocation.sku}
              </span>
              <Input
                aria-label={`Quantity for option ${allocation.sku}`}
                type="number"
                min={1}
                max={100}
                value={allocation.quantity}
                onChange={(event) =>
                  handleQuantityChange(allocation, event.target.value)
                }
                className="tw-h-7 tw-w-16 tw-px-2 tw-text-xs"
              />
              <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-muted-foreground">
                <input
                  type="checkbox"
                  aria-label={`Disable ${allocation.sku}`}
                  checked={allocation.disabled === true}
                  onChange={(event) =>
                    handleDisabledChange(allocation, event.target.checked)
                  }
                />
                Unavailable
              </label>
              <button
                type="button"
                aria-label={`Remove ${allocation.sku}`}
                onClick={() => handleRemoveOption(allocation.sku)}
                className="tw-text-muted-foreground hover:tw-text-foreground"
              >
                <X className="tw-h-3.5 tw-w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="tw-grid tw-grid-cols-[1fr_88px_auto] tw-gap-2">
        <Input
          aria-label={`SKU for ${groupLabel}`}
          value={skuDraft}
          maxLength={64}
          onChange={(event) => setSkuDraft(event.target.value)}
          placeholder="Option SKU"
        />
        <Input
          aria-label={`Quantity for ${groupLabel}`}
          type="number"
          min={1}
          max={100}
          value={quantityDraft}
          onChange={(event) => setQuantityDraft(event.target.value)}
          placeholder="Qty"
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleAddOption}
          disabled={isAddOptionDisabled}
          aria-label={`Add option to ${groupLabel}`}
        >
          Add option
        </Button>
      </div>
      {normalizedSkuDraft && isDuplicateSku && (
        <p className="tw-text-[11px] tw-text-destructive">
          This SKU is already used by another option.
        </p>
      )}
    </section>
  );
}

export function ProductGroupsEditor({
  productGroups,
  productAllocations,
  onProductGroupsChange,
  onProductAllocationsChange,
}: ProductGroupsEditorProps) {
  const validationErrors = validateProductGroupConfiguration(
    productGroups,
    productAllocations,
  );

  const handleAddGroup = () => {
    if (productGroups.length >= MAX_PRODUCT_GROUPS) return;
    onProductGroupsChange([
      ...productGroups,
      {
        groupId: createProductGroupId(productGroups),
        name: "",
        choiceCount: 1,
      },
    ]);
  };

  return (
    <div className="tw-grid tw-gap-3">
      {productGroups.map((productGroup) => (
        <ProductGroupOptionsEditor
          key={productGroup.groupId}
          productGroup={productGroup}
          productGroups={productGroups}
          productAllocations={productAllocations}
          onProductGroupsChange={onProductGroupsChange}
          onProductAllocationsChange={onProductAllocationsChange}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={handleAddGroup}
        disabled={productGroups.length >= MAX_PRODUCT_GROUPS}
        className="tw-justify-self-start"
      >
        <Plus className="tw-mr-1.5 tw-h-4 tw-w-4" />
        Add product group
      </Button>

      {validationErrors.length > 0 && (
        <div
          role="alert"
          className="tw-rounded-md tw-border tw-border-destructive/30 tw-bg-destructive/5 tw-px-3 tw-py-2"
        >
          <ul className="tw-list-disc tw-space-y-1 tw-pl-4 tw-text-[11px] tw-text-destructive">
            {validationErrors.map((validationError) => (
              <li key={validationError}>{validationError}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
